import { useState, useEffect, useCallback } from 'react'
import { useParams, Link } from 'react-router-dom'
import { PDFViewer, pdf } from '@react-pdf/renderer'
import { supabase } from '../../../api/supabase'
import { useAuth } from '../../../contexts/AuthContext'
import WarehouseLayout from '../WarehouseLayout'
import type { Devanning, DevanningItem, DevanningOutput } from '../types'
import { fmtDate, fmtDateTime } from '../types'
import { BlankDevanSheetPDF, type BlankSheetRow } from './BlankDevanSheet'

// Matches Workflow.tsx's splitSortKey — group names are "Group N" (or legacy "Devan Split N").
function groupSortKey(name: string): number {
  const m = name.match(/(?:devan split|group)\s+(\d+)/i)
  return m ? parseInt(m[1]) : Infinity
}

// #283 renamed "Devan Split N" -> "Group N"; legacy plans predating that rename
// still carry the old name in the DB — normalize on display.
function cleanGroupName(name: string): string {
  return name.replace(/\s*[—–-].*$/, '').trim().replace(/^Devan Split\s+/i, 'Group ')
}

// The Devan Report is the completed-devanning counterpart to the blank Devan
// Sheet (BlankDevanSheet.tsx) — same one-row-per-group layout, but filled with
// actual recorded data instead of being blank for the warehouse to hand-write.
function buildRows(items: DevanningItem[]): BlankSheetRow[] {
  const map = new Map<string, DevanningItem[]>()
  for (const it of items) {
    const key = it.group_name || it.split_no || '(No Group)'
    if (!map.has(key)) map.set(key, [])
    map.get(key)!.push(it)
  }
  const rows = Array.from(map.entries()).map(([groupName, its]) => {
    const first = its[0]
    const skipDetail = first?.skip_pallet_detail ?? true
    const pallets = Array(15).fill(0)
    for (const pd of (first?.pallets_detail || [])) {
      const idx = (((pd as any).p ?? (pd as any).pallet_no) as number) - 1
      if (idx >= 0 && idx < 15 && pd.ctns > 0) pallets[idx] = pd.ctns
    }
    const row: BlankSheetRow = {
      destination: first?.delivery_address || first?.delivery_method || '(No Address)',
      splitNo:     cleanGroupName(groupName),
      sysRefs:     [...new Set(its.map(i => i.system_ref).filter(Boolean))].sort().join('\n'),
      jobRefs:     [...new Set(its.map(i => i.plan_item?.client_ref).filter(Boolean))].sort().join('\n'),
      delMode:     first?.delivery_method || '',
      qty:         its.reduce((s, i) => s + (i.expected_cartons || 0), 0),
      wtKg:        its.reduce((s, i) => s + (i.expected_weight_kg || 0), 0) || null,
      cbm:         its.reduce((s, i) => s + (i.expected_cbm || 0), 0) || null,
      isHold:      its.some(i => i.hold_flag),
      skipDetail,
      actCartons:      skipDetail ? (first?.manual_carton_sum ?? 0) : (first?.actual_cartons_received ?? 0),
      actPalletDetail: pallets,
      actTotalPallets: skipDetail ? (first?.manual_pallet_total ?? 0) : pallets.filter(p => p > 0).length,
      notes:           first?.notes || '',
      pltLocation:     first?.plt_location || '',
    }
    return { groupName, row }
  })
  return rows
    .sort((a, b) => groupSortKey(a.groupName) - groupSortKey(b.groupName))
    .map(r => r.row)
}

// ISO datetime or DATE string -> <input type="date"> value (YYYY-MM-DD, local).
function toDateInputValue(iso: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

export function DevanningReportPDF({ devanning, items, warehouseCode }: { devanning: Devanning; items: DevanningItem[]; warehouseCode: string }) {
  const rows = buildRows(items)
  const jobRef = (devanning.plan as any)?.source_ll?.job_ref ?? devanning.plan?.job_ref ?? null

  return (
    <BlankDevanSheetPDF
      jobRef={jobRef || ''}
      containerNo={devanning.container_no}
      warehouseCode={warehouseCode}
      plannedDevanAt={(devanning.plan as any)?.planned_devan_at ?? devanning.plan?.eta_date ?? null}
      rows={rows}
      type="complete"
      devannedBy={devanning.devanned_by}
      devannedAt={devanning.devan_date_override ?? devanning.devanning_completed_at}
    />
  )
}

export default function DevanningReport() {
  const { id, did } = useParams<{ id: string; did: string }>()
  const { userEmail } = useAuth()
  const [devanning, setDevanning] = useState<Devanning | null>(null)
  const [items, setItems] = useState<DevanningItem[]>([])
  const [warehouseCode, setWarehouseCode] = useState('')
  const [savedOutput, setSavedOutput] = useState<DevanningOutput | null>(null)
  const [loading, setLoading] = useState(true)
  const [generating, setGenerating] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const [genError, setGenError] = useState<string | null>(null)
  const [editingDate, setEditingDate] = useState(false)
  const [savingDate, setSavingDate] = useState(false)

  const fetchData = useCallback(async () => {
    if (!did) return
    const [{ data: dev }, { data: its }, { data: out }, { data: wh }] = await Promise.all([
      supabase.from('devannings').select('*, plan:devanning_plans(container_no, job_ref, vessel, eta_date, planned_devan_at, source_ll:loading_lists(job_ref))').eq('id', did).single(),
      supabase.from('devanning_items').select('*, customer:customers(code, name), plan_item:devanning_plan_items(client_ref)').eq('devanning_id', did).order('group_name').order('id'),
      supabase.from('devanning_outputs').select('*').eq('devanning_id', did).eq('type', 'devanning_report').maybeSingle(),
      id ? supabase.from('warehouses').select('code').eq('id', id).maybeSingle() : Promise.resolve({ data: null }),
    ])
    setDevanning(dev as Devanning)
    setItems((its as DevanningItem[]) || [])
    setSavedOutput(out as DevanningOutput | null)
    setWarehouseCode((wh as any)?.code || '')
    setLoading(false)
  }, [did, id])

  useEffect(() => { fetchData() }, [fetchData])

  // Z50.7: "Regenerate & Save PDF" and "Download saved PDF" used to diverge —
  // the stored blob only ever reflected whatever was current the *last* time
  // someone clicked Regenerate, so a column/layout change (like Z50.3/Z50.6)
  // left the saved PDF stale until someone happened to regenerate again. Both
  // actions now share this one generate-and-save step, so there is only ever
  // one "current" version and no separate stale copy can be served.
  const generateAndSave = useCallback(async (): Promise<{ blob: Blob; downloadFileName: string }> => {
    if (!devanning) throw new Error('Devanning not loaded')
    const blob = await pdf(
      <DevanningReportPDF devanning={devanning} items={items} warehouseCode={warehouseCode} />
    ).toBlob()

    const jobRef = (devanning.plan as any)?.source_ll?.job_ref ?? devanning.plan?.job_ref ?? null
    const storageFileName = `${[jobRef, devanning.container_no].filter(Boolean).join('_')}_devan_report.pdf`
    const filePath = `devan-reports/${devanning.warehouse_id}/${storageFileName}`
    const { error: uploadErr } = await supabase.storage
      .from('warehouse-files')
      .upload(filePath, blob, { contentType: 'application/pdf', upsert: true, cacheControl: '0' })
    if (uploadErr) throw uploadErr

    const { data: { publicUrl } } = supabase.storage.from('warehouse-files').getPublicUrl(filePath, { download: storageFileName })
    // Supabase's storage CDN doesn't reliably invalidate its edge cache on upsert
    // overwrite, and filePath (and thus this URL) is deterministic across
    // regenerations — bust it so the browser/CDN can't serve last save's bytes.
    const cacheBustedUrl = `${publicUrl}${publicUrl.includes('?') ? '&' : '?'}v=${Date.now()}`

    await supabase.from('devanning_outputs').delete()
      .eq('devanning_id', did!).eq('type', 'devanning_report')
    await supabase.from('devanning_outputs').insert({
      devanning_id: did,
      type: 'devanning_report',
      delivery_address: null,
      pdf_url: cacheBustedUrl,
      generated_at: new Date().toISOString(),
      generated_by: userEmail,
    })
    await fetchData()

    // Z50.8: give the downloaded file a descriptive name (job ref + container +
    // devan date) instead of the storage path's UUID-derived default.
    const devanDate = devanning.devan_date_override ?? devanning.devanning_completed_at
    const dateStamp = devanDate ? toDateInputValue(devanDate).replace(/-/g, '') : ''
    const downloadFileName = `DevanReport_${[jobRef, devanning.container_no, dateStamp].filter(Boolean).join('_')}.pdf`

    return { blob, downloadFileName }
  }, [devanning, items, warehouseCode, did, userEmail, fetchData])

  const handleGenerate = async () => {
    setGenerating(true)
    setGenError(null)
    try {
      await generateAndSave()
    } catch (err: any) {
      setGenError(err.message || 'Failed to generate PDF')
    } finally {
      setGenerating(false)
    }
  }

  // Z50.8: downloads a freshly-generated blob directly (with a `download` attribute
  // forcing the filename) rather than following the stored pdf_url — sidesteps any
  // ambiguity in how Supabase Storage's Content-Disposition interacts with the CDN
  // cache-buster query param, and guarantees this is never the stale copy.
  const handleDownloadSaved = async () => {
    setDownloading(true)
    setGenError(null)
    try {
      const { blob, downloadFileName } = await generateAndSave()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = downloadFileName
      a.click()
      URL.revokeObjectURL(url)
    } catch (err: any) {
      setGenError(err.message || 'Failed to download PDF')
    } finally {
      setDownloading(false)
    }
  }

  const saveDateOverride = async (dateStr: string) => {
    setEditingDate(false)
    setSavingDate(true)
    try {
      const { error } = await supabase.from('devannings').update({
        devan_date_override: dateStr || null,
        devan_date_override_by: dateStr ? userEmail : null,
        devan_date_override_at: dateStr ? new Date().toISOString() : null,
      }).eq('id', did!)
      if (error) throw error
      await fetchData()
    } catch (err: any) {
      setGenError(err.message || 'Failed to save date')
    } finally {
      setSavingDate(false)
    }
  }

  return (
    <WarehouseLayout warehouseId={id!}>
      <div className="space-y-4">
        <div className="flex items-center gap-4 flex-wrap">
          <Link to={`/warehouse/${id}/devannings/${did}`} className="text-sm text-blue-600 hover:underline">← Workflow</Link>
          <Link to={`/warehouse/${id}/devannings/${did}/notes`} className="text-sm text-blue-600 hover:underline">Delivery Notes →</Link>
        </div>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            {(() => { const jr = (devanning?.plan as any)?.source_ll?.job_ref ?? devanning?.plan?.job_ref; return `Devanning Report${jr ? ` — ${jr}` : ''} — ${devanning?.container_no}` })()}
          </h2>
          <div className="flex items-center gap-3">
            <button onClick={handleDownloadSaved} disabled={downloading || generating || loading}
              className="text-sm text-blue-600 hover:underline disabled:opacity-50">
              {downloading ? 'Preparing download…' : 'Download saved PDF ↓'}
            </button>
            <button onClick={handleGenerate} disabled={generating || downloading || loading}
              className="px-4 py-2 text-sm font-semibold bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
              {generating ? 'Generating…' : savedOutput ? 'Regenerate & Save PDF' : 'Generate & Save PDF'}
            </button>
          </div>
        </div>
        {genError && <p className="text-red-600 text-sm">{genError}</p>}
        {devanning && (
          <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
            <span className="font-medium">Devanned Date:</span>
            {editingDate ? (
              <input
                type="date"
                autoFocus
                defaultValue={toDateInputValue(devanning.devan_date_override ?? devanning.devanning_completed_at)}
                disabled={savingDate}
                onBlur={e => saveDateOverride(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
                  if (e.key === 'Escape') setEditingDate(false)
                }}
                className="border border-blue-400 rounded px-1.5 py-0.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white dark:bg-gray-700 dark:text-white"
              />
            ) : (
              <button onClick={() => setEditingDate(true)} disabled={savingDate}
                className="group inline-flex items-center gap-1 hover:underline disabled:opacity-50">
                {savingDate ? 'Saving…' : fmtDate(devanning.devan_date_override ?? devanning.devanning_completed_at)}
                <span className="opacity-0 group-hover:opacity-100 text-xs">✎</span>
              </button>
            )}
            {devanning.devan_date_override && (
              <span className="text-xs text-gray-400">
                (corrected from {fmtDate(devanning.devanning_completed_at)} by {devanning.devan_date_override_by} on {fmtDateTime(devanning.devan_date_override_at)})
              </span>
            )}
          </div>
        )}
        {savedOutput && (
          <p className="text-xs text-gray-400">Last saved: {fmtDateTime(savedOutput.generated_at)} by {savedOutput.generated_by}</p>
        )}
        {loading ? (
          <div className="py-16 text-center text-gray-400">Loading...</div>
        ) : (
          <>
            {/* Z50.8: react-pdf's own toolbar download icon (inside the viewer below)
                downloads from a blob URL with no useful filename — steer staff to the
                button above instead of letting them discover that the hard way. */}
            <p className="flex items-center gap-1.5 text-xs text-gray-400 dark:text-gray-500">
              <span aria-hidden="true">🛈</span>
              To download this report with the correct filename, use the "Download saved PDF"
              button above — not the download arrow inside the preview below (it gives the
              file a garbled name).
            </p>
            <div className="rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700" style={{ height: '85vh' }}>
              <PDFViewer width="100%" height="100%" showToolbar>
                <DevanningReportPDF devanning={devanning!} items={items} warehouseCode={warehouseCode} />
              </PDFViewer>
            </div>
          </>
        )}
      </div>
    </WarehouseLayout>
  )
}
